import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { Formik, FormikProps } from 'formik'
import { Button, ModalInjectedProps } from '@sketch/components'
import {
  GetWorkspaceProfileIdentifierDocument,
  useCreateWorkspaceProfileMutation,
  useGetSharesForDocumentSelectionQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { routes } from '@sketch/modules-common'
import {
  Modal,
  ModalBody,
  OrderedBreadcrumbs,
  InfoNoteContainer,
  InformationIcon,
} from './ProfileModal.styles'

import { useProfileShortURL } from '../../hooks/useProfileShortURL'
import { initialValues, validationSchema } from './form'
import { useGetWorkspaceProfile } from '../../hooks'

import { IntroductionStep } from './IntroductionStep'
import { CreateProfileDetailsStep } from './CreateProfileDetailsStep'
import { SelectDocumentsStep } from './SelectDocumentsStep'

interface BreadcrumbsProps {
  step: number
}

const Breadcrumbs = ({ step }: BreadcrumbsProps) => {
  const breadcrumbs = [
    { content: 'Public Profile' },
    { content: 'Details' },
    { content: 'Documents' },
  ]

  return (
    <OrderedBreadcrumbs
      currentCrumb={step}
      crumbs={breadcrumbs}
      showAllInMobile
    />
  )
}

interface CreateProfileModalProps extends ModalInjectedProps {
  workspace: WorkspaceMinimalFragment
}

export const CreateProfileModal = ({
  workspace,
  hideModal,
}: CreateProfileModalProps) => {
  const history = useHistory()

  const profileResult = useGetWorkspaceProfile({
    variables: {
      workspaceIdentifier: workspace.identifier,
    },
  })
  const profileData = profileResult.profile
  const shortUrlName = profileData?.shortUrlName || ''
  const description = profileData?.description || ''

  const [step, setStep] = useState(0)

  const { currentShortUrl } = useProfileShortURL(shortUrlName)
  const composedInitialFormValue = {
    ...initialValues,
    shortUrlName: currentShortUrl,
    description,
  }

  const [createWorkspaceProfile, { loading: createWorkspaceProfileLoading }] =
    useCreateWorkspaceProfileMutation({
      onError: 'show-toast',
      onCompleted: () => {
        history.push(
          routes.WORKSPACE_PROFILE_EDIT.create({
            workspaceId: workspace.identifier,
          })
        )
      },
    })

  const { data, loading: loadingShares } =
    useGetSharesForDocumentSelectionQuery({
      variables: {
        identifier: workspace.identifier,
        search: {
          filter: 'EXCLUDE_PERSONAL_DRAFTS',
        },
      },
      fetchPolicy: 'cache-and-network',
    })

  const loading = createWorkspaceProfileLoading || loadingShares

  const prevStep = () => {
    setStep(prev => prev - 1)
  }

  const nextStep = () => {
    setStep(prev => prev + 1)
  }

  const renderStep = (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0:
        return <IntroductionStep />
      case 1: {
        return (
          <CreateProfileDetailsStep
            workspace={workspace}
            formikBag={formikBag}
          />
        )
      }
      case 2: {
        return (
          <SelectDocumentsStep workspace={workspace} formikBag={formikBag} />
        )
      }
    }
  }

  const handleSubmitButtonAction = async (formikBag: FormikProps<any>) => {
    switch (step) {
      case 0: {
        nextStep()
        break
      }
      case 1: {
        const { shares, ...stepOneValues } = initialValues
        // Given that shortUrlName validation is not part of formik, we need
        // to make sure the there's no current error with shortUrlName before
        // triggering validation
        if (formikBag.errors.shortUrlName) {
          return
        }
        const errors = await formikBag.validateForm()
        formikBag.setTouched(
          Object.keys(stepOneValues).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {} as Record<string, boolean>
          )
        )

        if (Object.keys(errors).length === 0) {
          nextStep()
        }
        break
      }
      case 2: {
        const { values } = formikBag
        const { selectAll, checkProfileURLLoading, ...formValues } = values

        createWorkspaceProfile({
          variables: {
            workspaceIdentifier: workspace.identifier,
            input: { ...formValues },
          },
          refetchQueries: [
            {
              query: GetWorkspaceProfileIdentifierDocument,
              variables: { identifier: workspace.identifier },
            },
          ],
          awaitRefetchQueries: true,
        })

        break
      }
    }
  }

  const getSubmitButtonText = () => {
    switch (step) {
      case 0:
        return 'Get Started'
      case 1:
        return 'Choose Documents'
      case 2:
        return 'Create Public Profile'
    }
  }

  const renderFooterNote = () => {
    if (step === 2) {
      return (
        <InfoNoteContainer>
          <InformationIcon />
          Documents on your profile will be publicly visible
        </InfoNoteContainer>
      )
    }
  }

  const sharesWithPublication = useMemo(
    () =>
      data?.workspace.shares?.entries
        ?.filter(({ publication }) => !!publication)
        .map(({ identifier }) => identifier),
    [data]
  )

  return (
    <Formik
      initialValues={{
        ...composedInitialFormValue,
        shares: sharesWithPublication,
      }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      // eslint-disable-next-line no-console
      onSubmit={() => {}}
    >
      {formikBag => (
        <Modal onCancel={hideModal}>
          <ModalBody>
            <Breadcrumbs step={step} />

            {renderStep(formikBag)}
          </ModalBody>
          <Modal.Footer leftContent={renderFooterNote()}>
            <Button
              variant="secondary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              size="40"
              onClick={step === 0 ? hideModal : prevStep}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="primary"
              disabled={loading || formikBag.values.checkProfileURLLoading}
              loading={loading}
              size="40"
              onClick={() => handleSubmitButtonAction(formikBag)}
            >
              {getSubmitButtonText()}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}
